import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import * as React from "react";
import { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    variant = "variant",
    label = "label",
    alignment = "alignment",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.alignment]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        session: state.context.session,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const CoopButton: React.FunctionComponent<Props> = ({ fields, session }: Props) => {
    const toasterContext = useContext(ToasterContext);

    const generateCoopStatementPdf = () => {
        if (!session?.isAuthenticated) {
            toasterContext.addToast({
                body: `${translate("Login Required")}`,
                messageType: "warning",
            });

            return;
        }

        const unilogTokenApi = `${window.origin}/api/auersteel/v1/coopstatement/pdf`;

        toasterContext.addToast({
            body: `${translate("The download has started. You'll be notified when it is complete.")}`,
            messageType: "info",
            timeoutLength: 10000,
        });

        fetch(unilogTokenApi, {
            method: "GET",
        }).then((response: Response) => {
            const filename = response?.headers?.get("Content-Disposition")?.split("filename=")[1];
            response.blob().then(blob => {
                if (blob.size > 25) {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `${filename ?? "CO-OP_Statement"}.pdf`;
                    link.click();

                    toasterContext.addToast({
                        body: `${translate("Downloading Complete")}`,
                        messageType: "success",
                    });
                } else {
                    toasterContext.addToast({
                        body: `${translate("No Statement Found")}`,
                        messageType: "warning",
                    });
                }
            });
        });
    };

    const wrapperStyles = {
        css: css`
            width: 100%;
            display: flex;
            justify-content: ${fields.alignment ?? "left"};
        `,
    };

    return (
        <StyledWrapper {...wrapperStyles}>
            <Button variant={fields.variant} onClick={() => generateCoopStatementPdf()}>
                {fields.label}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CoopButton),
    definition: {
        group: "Common",
        icon: "Button",
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "VIEW CO-OP STATEMENT",
                fieldType: "Translatable",
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
